import type { SoftConsistencyStatus, UpdateSoftConsistencyInput } from '@/api/projectApi'

export type SoftConsistencyToggleTarget = 'master' | 'ooc' | 'setting' | 'emotion'

export function getSoftConsistencyChecked(
  status: SoftConsistencyStatus | undefined,
  target: SoftConsistencyToggleTarget,
): boolean {
  if (!status) return false
  if (target === 'master') return status.master.value ?? status.master.effective
  return status.dimensions[target].value ?? status.dimensions[target].effective
}

export function isSoftConsistencyControlDisabled(
  status: SoftConsistencyStatus | undefined,
  isPending: boolean,
): boolean {
  return isPending || !status?.globally_available
}

export function getSoftConsistencyDescriptionKey(
  status: SoftConsistencyStatus | undefined,
  target: SoftConsistencyToggleTarget,
): string {
  if (status && !status.globally_available) return 'detail.softConsistency.globallyDisabled'
  switch (target) {
    case 'master':
      return 'detail.softConsistency.masterHint'
    case 'ooc':
      return 'detail.softConsistency.oocHint'
    case 'setting':
      return 'detail.softConsistency.settingHint'
    case 'emotion':
      return 'detail.softConsistency.emotionHint'
  }
}

export function buildSoftConsistencyToggleBody(
  target: SoftConsistencyToggleTarget,
  checked: boolean,
): UpdateSoftConsistencyInput {
  switch (target) {
    case 'master':
      return { enabled: checked }
    case 'ooc':
      return { ooc_enabled: checked }
    case 'setting':
      return { setting_enabled: checked }
    case 'emotion':
      return { emotion_enabled: checked }
  }
}

/**
 * 软一致性是否【任何一个维度】都没开。
 *
 * 用于把看板上的「未检测」拆成两种情况 —— 它们看起来一样，但用户该做的事完全不同：
 *   · 功能没开   ⇒ 去设置里打开（等下去也不会有结果）
 *   · 已开但没跑 ⇒ 等待或触发检测
 * 一句话覆盖两种情况，就是「租界」那条冲突同样的病：判据没错，但让人无从下手。
 */
export function isSoftConsistencyAllDisabled(status: SoftConsistencyStatus | undefined): boolean {
  if (!status) return false
  if (!status.globally_available) return true
  const on = (target: SoftConsistencyToggleTarget) => getSoftConsistencyChecked(status, target)
  if (!on('master')) return true
  return !on('ooc') && !on('setting') && !on('emotion')
}
